// MoodEngine.js — Pazurumon Mood Engine Phase 1 + 2
// Phase 1: individual character lines
// Phase 2: mini-dialogues between 2 characters
// Phase 3: rare lines, emotional escalation, multilingual switching

import moodData from '../data/mood_data.json';
import { I18n }  from './I18n.js';

const TRIGGER_CHANCE = moodData.config.triggerChance;
const COOLDOWNS      = moodData.config.cooldowns;
const DIST           = moodData.config.contentTypeDistribution;

// Mood character → MonsterBar index
const SPEAKER_INDEX = { pyron: 0, voltrix: 1, aquyra: 2, sylrana: 3, athena: 4 };

export class MoodEngine {
  constructor(scene, monsterBar) {
    this.scene      = scene;
    this.monsterBar = monsterBar;

    // Cooldown timestamps
    this._globalCooldownUntil    = 0;
    this._speakerCooldown        = {}; // { speakerId: timestamp }
    this._interactionCooldown    = {}; // { interactionId: timestamp }

    // Anti-repetition history
    this._recentLineIds          = []; // last N individual line ids
    this._recentInteractionIds   = []; // last N interaction ids
    this._recentSpeakers         = []; // last N speaker ids

    // Fail streak tracking
    this._failStreak = 0;

    // Idle tracking
    this._lastMoveTime = Date.now();
  }

  onPlayerAction() {
    this._lastMoveTime = Date.now();
    this._failStreak   = 0;
  }

  onFailedMove() {
    this._failStreak++;
  }

  tryMoodEvent(triggerType, gameState = {}) {
    if (!this._canShow(gameState)) return;

    const baseChance = TRIGGER_CHANCE[triggerType] ?? 0;
    const progress   = gameState.goalProgress ?? 0;
    const cfg        = moodData.config.moodEscalation;
    let   chance     = baseChance;
    if (progress >= cfg.lateGameProgressThreshold) {
      chance += cfg.lateGameTriggerBoost[triggerType]     ?? 0;
      chance -= cfg.lateGameTriggerReduction[triggerType] ?? 0;
    }

    if (Math.random() > chance) return;

    // Roll content type: individual / interaction / rare(→individual for now)
    const contentType = this._rollContentType();

    if (contentType === 'interaction') {
      const interaction = this._pickInteraction(triggerType);
      if (interaction) {
        this._presentInteraction(interaction);
        return;
      }
      // Fallback to individual if no interaction available
    }

    const line = this._pickLine(triggerType);
    if (line) this._presentLine(line);
  }

  checkIdle(gameState = {}) {
    if (!this._canShow(gameState)) return;
    const elapsed = Date.now() - this._lastMoveTime;
    if (elapsed >= 8000) {
      this.tryMoodEvent('idle_long', gameState);
    } else if (elapsed >= 5000) {
      this.tryMoodEvent('idle_light', gameState);
    }
  }

  // ── Content type roll ────────────────────────────────────────────────────
  _rollContentType() {
    const r = Math.random();
    if (r < DIST.individual)                    return 'individual';
    if (r < DIST.individual + DIST.interaction) return 'interaction';
    return 'rare'; // Phase 3 — falls back to individual
  }

  // ── Blocking check ───────────────────────────────────────────────────────
  _canShow(gameState) {
    const now = Date.now();
    if (now < this._globalCooldownUntil) return false;
    if (gameState.isBusy)                return false;
    if (gameState.activeModal)           return false;
    if (gameState.moodVisible)           return false;
    return true;
  }

  // ── Individual line pick ─────────────────────────────────────────────────
  _pickLine(triggerType) {
    const now = Date.now();
    const candidates = moodData.individualLines.filter(line => {
      if (!line.triggers.includes(triggerType))              return false;
      if (this._recentLineIds.includes(line.id))             return false;
      if ((this._speakerCooldown[line.speaker] ?? 0) > now)  return false;
      return true;
    });
    if (candidates.length === 0) return null;
    return this._weightedPick(candidates);
  }

  // ── Interaction pick ─────────────────────────────────────────────────────
  _pickInteraction(triggerType) {
    const now = Date.now();
    const candidates = moodData.interactions.filter(inter => {
      if (!inter.triggers.includes(triggerType))                  return false;
      if (this._recentInteractionIds.includes(inter.id))          return false;
      if ((this._interactionCooldown[inter.id] ?? 0) > now)       return false;
      // Check both speakers not on cooldown
      for (const line of inter.lines) {
        if ((this._speakerCooldown[line.speaker] ?? 0) > now)     return false;
      }
      return true;
    });
    if (candidates.length === 0) return null;
    return this._weightedPick(candidates);
  }

  _weightedPick(candidates) {
    const total = candidates.reduce((s, c) => s + (c.weight ?? 1), 0);
    let r = Math.random() * total;
    for (const c of candidates) {
      r -= (c.weight ?? 1);
      if (r <= 0) return c;
    }
    return candidates[candidates.length - 1];
  }

  // ── Present individual line ───────────────────────────────────────────────
  _presentLine(line) {
    const locale      = I18n.getLocale();
    const text        = line.text?.[locale] || line.text?.['en-US'] || Object.values(line.text ?? {}).find(v => v) || '';
    if (!text) return;

    const charData    = moodData.characters[line.speaker];
    const displayName = charData?.displayName?.[locale] || line.speaker;
    const charColor   = charData?.color || '#e2e8f0';
    const idx         = SPEAKER_INDEX[line.speaker] ?? 0;
    const position    = this._getSpeakerPosition(idx);

    this.scene.moodPresenter?.show({ text, speaker: displayName, speakerId: line.speaker, color: charColor, x: position.x, y: position.y });

    const now = Date.now();
    this._globalCooldownUntil           = now + COOLDOWNS.globalMs;
    this._speakerCooldown[line.speaker] = now + COOLDOWNS.speakerMs;

    this._recentLineIds.push(line.id);
    if (this._recentLineIds.length > COOLDOWNS.recentIndividualHistorySize) this._recentLineIds.shift();
    this._recentSpeakers.push(line.speaker);
    if (this._recentSpeakers.length > COOLDOWNS.recentSpeakerHistorySize) this._recentSpeakers.shift();
  }

  // ── Present interaction (2-line sequential dialogue) ─────────────────────
  _presentInteraction(interaction) {
    const locale = 'pt-PT';

    const resolvedLines = interaction.lines.map(line => {
      const text        = line.text?.[locale] || line.text?.['en-US'] || '';
      const charData    = moodData.characters[line.speaker];
      const idx         = SPEAKER_INDEX[line.speaker] ?? 0;
      const pos         = this._getSpeakerPosition(idx);
      return {
        text,
        speaker:   charData?.displayName?.[locale] || line.speaker,
        speakerId: line.speaker,
        color:     charData?.color || '#e2e8f0',
        x:         pos.x,
        y:         pos.y,
      };
    }).filter(l => l.text);

    if (resolvedLines.length < 2) return;

    this.scene.moodPresenter?.showInteraction(resolvedLines);

    const now = Date.now();
    this._globalCooldownUntil             = now + COOLDOWNS.interactionMs;
    this._interactionCooldown[interaction.id] = now + COOLDOWNS.interactionMs;
    for (const line of interaction.lines) {
      this._speakerCooldown[line.speaker] = now + COOLDOWNS.speakerMs;
    }

    this._recentInteractionIds.push(interaction.id);
    if (this._recentInteractionIds.length > COOLDOWNS.recentInteractionHistorySize) this._recentInteractionIds.shift();
  }

  // ── Compute balloon anchor position ──────────────────────────────────────
  _getSpeakerPosition(charIndex) {
    const item = this.monsterBar?.items?.[charIndex];
    if (!item?.card) return { x: this.scene.scale.width / 2, y: this.scene.scale.height - 230 };
    return { x: item.card.x, y: item.card.y - 100 };
  }
}
